import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, Quote, ShieldCheck, Zap, Star } from "lucide-react";
import { SERVICES, STATS, TESTIMONIALS } from "@/data/content";

export default function Home() {
  return (
    <div data-testid="home-page" className="relative">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 grid-bg radial-fade pointer-events-none" />
        <div
          className="absolute -top-32 -left-20 w-[480px] h-[480px] rounded-full blur-3xl opacity-30 pointer-events-none"
          style={{ background: "radial-gradient(circle, #00D4FF 0%, transparent 70%)" }}
        />
        <div
          className="absolute -bottom-40 -right-20 w-[520px] h-[520px] rounded-full blur-3xl opacity-20 pointer-events-none"
          style={{ background: "radial-gradient(circle, #00D4FF 0%, transparent 70%)" }}
        />

        <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-20 md:pt-28 pb-20 md:pb-32">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 space-y-8">
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#00D4FF]/10 border border-[#00D4FF]/30"
                data-testid="hero-badge"
              >
                <Sparkles size={14} className="text-[#00D4FF]" />
                <span className="font-mono-tech text-[11px] tracking-[0.2em] uppercase text-[#00D4FF]">
                  AI · Data · Engineering
                </span>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05, duration: 0.6 }}
                className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.05] text-white"
                data-testid="hero-headline"
              >
                We transform ideas into{" "}
                <span className="text-gradient-cyan">intelligent solutions</span>
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15, duration: 0.6 }}
                className="text-lg text-slate-300 max-w-xl leading-relaxed"
                data-testid="hero-subheadline"
              >
                TriNexa is a Bangalore-based engineering studio building
                production-grade AI, data platforms, and cloud-native software
                for ambitious teams.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.25, duration: 0.6 }}
                className="flex flex-wrap items-center gap-4"
              >
                <Link
                  to="/contact"
                  data-testid="hero-cta-primary"
                  className="group inline-flex items-center gap-2 px-6 py-3.5 rounded-lg bg-[#00D4FF] text-[#050B14] font-semibold hover:bg-[#33DDFF] transition-colors"
                >
                  Start a project
                  <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link
                  to="/services"
                  data-testid="hero-cta-secondary"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg border border-white/15 text-white hover:border-[#00D4FF] hover:text-[#00D4FF] transition"
                >
                  Explore services
                </Link>
              </motion.div>

              <div className="pt-8 grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-2xl" data-testid="hero-stats">
                {STATS.map((s) => (
                  <div key={s.label}>
                    <div className="font-display text-3xl font-bold text-white">{s.value}</div>
                    <div className="text-xs text-slate-400 mt-1">{s.label}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-5 relative">
              <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.7 }}
                className="relative aspect-square rounded-3xl overflow-hidden border border-white/10 bg-[#0A1128] glow-cyan"
              >
                <img
                  src="https://images.unsplash.com/photo-1674027444485-cec3da58eef4?crop=entropy&cs=srgb&fm=jpg&w=900&q=80"
                  alt="AI network sphere"
                  className="absolute inset-0 w-full h-full object-cover opacity-70"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-[#050B14] via-transparent to-transparent" />
                <div className="absolute bottom-6 left-6 right-6 flex items-center gap-3 p-4 rounded-xl bg-[#050B14]/70 backdrop-blur-md border border-white/10">
                  <div className="w-10 h-10 rounded-lg bg-[#00D4FF]/15 border border-[#00D4FF]/30 flex items-center justify-center">
                    <Zap size={18} className="text-[#00D4FF]" />
                  </div>
                  <div className="text-sm">
                    <div className="text-white font-semibold">Production-grade AI</div>
                    <div className="text-slate-400 text-xs">From prototype to scale</div>
                  </div>
                </div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="absolute -top-6 -left-6 hidden sm:block px-4 py-3 rounded-xl bg-[#0A1128] border border-white/10 glow-cyan"
              >
                <div className="font-mono-tech text-[10px] tracking-widest text-[#00D4FF]">UPTIME</div>
                <div className="font-display text-lg text-white">99.99%</div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* OVERVIEW */}
      <section className="relative py-24 border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <p className="font-mono-tech text-xs tracking-[0.3em] uppercase text-[#00D4FF] mb-4">
              About TriNexa
            </p>
            <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-white leading-tight">
              An engineering studio for the AI era.
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-6 text-slate-300 leading-relaxed">
            <p>
              We partner with founders, product leaders and enterprises to build
              software that thinks — combining the rigour of modern engineering
              with the imagination of AI.
            </p>
            <p>
              From data platforms and ML systems to cloud-native applications
              and developer platforms, TriNexa ships outcomes that compound.
            </p>
            <div className="grid sm:grid-cols-2 gap-4 pt-2">
              <div className="flex items-start gap-3">
                <ShieldCheck className="text-[#00D4FF] shrink-0 mt-0.5" size={20} />
                <div>
                  <div className="text-white font-semibold">Senior-led delivery</div>
                  <div className="text-sm text-slate-400">No layers, no overhead.</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Star className="text-[#00D4FF] shrink-0 mt-0.5" size={20} />
                <div>
                  <div className="text-white font-semibold">Production focused</div>
                  <div className="text-sm text-slate-400">Observable, reliable, evaluated.</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES HIGHLIGHT */}
      <section className="relative py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div>
              <p className="font-mono-tech text-xs tracking-[0.3em] uppercase text-[#00D4FF] mb-3">
                What we do
              </p>
              <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-white">
                Services designed for outcomes.
              </h2>
            </div>
            <Link
              to="/services"
              data-testid="services-view-all"
              className="inline-flex items-center gap-2 text-[#00D4FF] hover:text-white transition"
            >
              View all services <ArrowRight size={16} />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {SERVICES.map((s) => {
              const Icon = s.icon;
              return (
                <motion.div
                  key={s.slug}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="group p-6 rounded-2xl bg-[#0A1128] border border-white/10 hover-glow"
                  data-testid={`service-card-${s.slug}`}
                >
                  <div className="w-11 h-11 rounded-lg bg-[#00D4FF]/10 border border-[#00D4FF]/30 flex items-center justify-center mb-5 group-hover:bg-[#00D4FF]/20 transition">
                    <Icon size={20} className="text-[#00D4FF]" />
                  </div>
                  <h3 className="font-display text-lg font-semibold text-white mb-2">
                    {s.title}
                  </h3>
                  <p className="text-sm text-slate-400 leading-relaxed">{s.short}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="relative py-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <p className="font-mono-tech text-xs tracking-[0.3em] uppercase text-[#00D4FF] mb-3">
              Testimonials
            </p>
            <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-white">
              Trusted by builders & operators.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                data-testid={`testimonial-${i}`}
                className="p-7 rounded-2xl bg-[#0A1128] border border-white/10 hover-glow"
              >
                <Quote className="text-[#00D4FF] mb-4" size={22} />
                <p className="text-slate-200 leading-relaxed">{t.quote}</p>
                <div className="mt-6 pt-5 border-t border-white/10">
                  <div className="text-white font-semibold">{t.name}</div>
                  <div className="text-sm text-slate-400">{t.title}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="relative overflow-hidden rounded-3xl p-10 md:p-14 bg-gradient-to-br from-[#0A1128] to-[#050B14] border border-[#00D4FF]/20 glow-cyan">
            <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full blur-3xl opacity-30"
              style={{ background: "radial-gradient(circle, #00D4FF 0%, transparent 70%)" }} />
            <div className="relative grid md:grid-cols-3 gap-8 items-center">
              <div className="md:col-span-2">
                <p className="font-mono-tech text-xs tracking-[0.3em] uppercase text-[#00D4FF] mb-3">
                  Let's build something intelligent
                </p>
                <h3 className="font-display text-3xl md:text-4xl font-bold text-white leading-tight">
                  Ready to ship your next-generation product?
                </h3>
              </div>
              <div className="flex md:justify-end">
                <Link
                  to="/contact"
                  data-testid="cta-home-contact"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg bg-[#00D4FF] text-[#050B14] font-semibold hover:bg-[#33DDFF] transition"
                >
                  Talk to us <ArrowRight size={18} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
